import React, { useState, useEffect } from 'react';
import { _t } from "../i18n";
import GoogleTranslate from './GoogleTranslate';

// Change item component for individual changelog entries
const ChangeItem = ({ change }) => {
  const parseChange = (raw) => {
    const tagBlock = raw.match(/^\s*(\[[^\]]+\]\s*)+/);
    if (!tagBlock) return { tags: [], text: raw.trim() };

    const tagText = tagBlock[0];
    const tags = (tagText.match(/\[[^\]]+\]/g) || []).map((t) => t.slice(1, -1).trim());
    const text = raw.slice(tagText.length).trim();

    return { tags, text: text || raw.trim() };
  };

  const getBadgeForTag = (tag) => {
    const normalized = tag.toUpperCase();

    const categories = {
      FEATURE: { label: _t('Feature'), color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
      CLIENT: { label: _t('Client'), color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
      ADMIN: { label: _t('Admin'), color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
      IMPROVEMENT: { label: _t('Improvement'), color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
      DEBUG: { label: _t('Debug'), color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' }
    };

    if (categories[normalized]) return categories[normalized];

    // Generic tags (modules like DN/GPS/GI/Visits, etc.)
    return {
      label: tag,
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
    };
  };

  const { tags, text } = parseChange(change);

  return (
    <li className="text-gray-600 dark:text-gray-300 text-sm flex items-start gap-2 min-w-0 w-full text-left">
      <div className="flex items-start gap-1.5 flex-wrap shrink-0 pt-[1px]">
        {tags.map((tag) => {
          const badge = getBadgeForTag(tag);
          return (
            <span
              key={`${tag}-${badge.label}`}
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium w-fit whitespace-nowrap ${badge.color}`}
            >
              {badge.label}
            </span>
          );
        })}
      </div>
      <span className="leading-snug break-words min-w-0 flex-1 text-left">{text}</span>
    </li>
  );
};

// Release entry component for a version release
const ReleaseEntry = ({ release, isLast }) => {
  return (
    <div>
      <div className="mb-6">
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>

          <span className="font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap">
            v{release.version}
          </span>

          <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {new Date(release.date).toLocaleDateString('fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </span>

          {release.adminOnly && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 whitespace-nowrap">
              {_t('Admin')}
            </span>
          )}
        </div>

        <ul className="space-y-4 md:space-y-3">
          {release.changes.map((change, changeIndex) => (
            <ChangeItem key={changeIndex} change={change} />
          ))}
        </ul>
      </div>

      {/* Séparateur (sauf pour le dernier élément) */}
      {!isLast && (
        <div className="border-t border-dotted border-gray-200 dark:border-gray-400 my-6 w-[75px] mx-auto"></div>
      )}
    </div>
  );
};

// Main Changelog component
function Changelog({ currentUser, showHeader = true }) {
  const [changelog, setChangelog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/changelog.json')
      .then(response => response.json())
      .then(data => {
        setChangelog(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Erreur lors du chargement du changelog:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="">
        <div className="w-100 text-center text-gray-600 dark:text-gray-300">
          {_t('Loading updates...')}
        </div>
      </div>
    );
  }

  if (!changelog || !Array.isArray(changelog) || changelog.length === 0)
    return null;

  const sortedChangelog = [...changelog].reverse();

  return (
    <div className="text-left">
      {showHeader && (
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {_t('Latest updates')}
              </h2>
            </div>

          </div>
          <div className="border-b border-gray-200 dark:border-gray-700"></div>
        </div>
      )}

      {sortedChangelog.map((release, index) => {
        if (!currentUser.isadmin && !currentUser.issuperadmin && release.adminOnly) {
          return null;
        }

        return <ReleaseEntry
          key={release.version}
          release={release}
          isLast={index === sortedChangelog.length - 1}
        />
      })}
    </div>
  );
}

export default Changelog;